import type { HTMLAttributes } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { cn } from '@/lib/cn';

import { getAlphaColor } from './colors/color.utils';
import zIndex from './zIndex';

export const ellipsisText = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: pre-wrap;
  word-break: break-all;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
`;

export const EllipsisDiv = styled.div`
  ${ellipsisText};
`;

export const multilineEllipsis = (line: number) => {
  return css`
    ${ellipsisText};
    white-space: pre-wrap;
    word-break: break-all;
    display: -webkit-box;
    -webkit-line-clamp: ${line};
    -webkit-box-orient: vertical;
  `;
};

export const customScrollbar = css`
  scrollbar-width: thin;
  scrollbar-color: ${(props) => props.theme.colors.gray500} transparent;

  &::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${(props) => props.theme.colors.gray500};
    border-radius: 999px;
    border: 3px solid transparent;
    background-clip: padding-box;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-corner {
    background: transparent;
  }
`;

export const hiddenScrollbar = css`
  &::-webkit-scrollbar {
    display: none;
  }
`;

export const scrollWrapper = css`
  overflow: auto;
  ${hiddenScrollbar};
`;

export const shadow = {
  bottomLine: css`
    box-shadow: inset 0px -1px 0px 0px ${(props) => props.theme.colors.ND04};
  `,
  topLine: css`
    box-shadow: inset 0px 1px 0px 0px ${(props) => props.theme.colors.ND04};
  `,
  rightLine: css`
    box-shadow: inset -1px 0px 0px 0px ${(props) => props.theme.colors.ND04};
  `,
  leftLine: css`
    box-shadow: inset 1px 0px 0px 0px ${(props) => props.theme.colors.ND04};
  `,
  topBottomLine: css`
    box-shadow:
      inset 0px -1px 0px 0px ${(props) => props.theme.colors.ND04},
      inset 0px 1px 0px 0px ${(props) => props.theme.colors.ND04};
  `,
};

export const media = {
  mobileS: '@media (max-width: 320px)',
  mobileM: '@media (max-width: 375px)',
  mobile: '@media (max-width: 430px)',
  tablet: '@media (max-width: 1024px)',
  hd: '@media (max-width: 1280px)',
  laptop: '@media (max-width: 1440px)',
  fhd: '@media (max-width: 1920px)',
  qhd: '@media (max-width: 2560px)',
  uhd: '@media (max-width: 3840px)',
  custom: (width: number) => `@media (max-width: ${width}px)`,
};

export const animation = {
  fadeIn: keyframes`
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  `,
  fadeOut: keyframes`
    0% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  `,
  slideIn: keyframes`
  0% {
    opacity: 0;
    transform: translateY(5px);
  }
  100% {
    opacity: 1;
    transform: translateY(0px);
  }
`,
  slideOut: keyframes`
  0% {
    opacity: 1;
    transform: translateY(0px);
  }
  100% {
    opacity: 0;
    transform: translateY(5px);
  }
`,
  hSlideIn: keyframes`
  0% {
    opacity: 0;
    transform: translateX(-20px);
  }
  100% {
    opacity: 1;
    transform: translateX(0px);
  }
`,
  hSlideOut: keyframes`
  0% {
    opacity: 0;
    transform: translateX(0px);
  }
  100% {
    opacity: 1;
    transform: translateX(-20px);
  }
`,
  modalIn: keyframes`
    0% {
      opacity: 0;
      transform: scale(0.98);
      transform-origin: center left;
    }
    100% {
      opacity: 1;
      transform: scale(1);
      transform-origin: center left;
    }
  `,
  modalOut: keyframes`
    0% {
      opacity: 1;
      transform: scale(1);
      transform-origin: center left;
    }
    100% {
      opacity: 0;
      transform: scale(0.98);
      transform-origin: center left;
    }
  `,
  scaleIn: keyframes`
    0% {
      opacity: 0;
      transform: scale(0.8);
    }
    100% {
      opacity: 1;
      transform: scale(1);
    }
  `,
  scaleOut: keyframes`
  0% {
      opacity: 1;
      transform: scale(1);
    }
    100% {
      opacity: 0;
      transform: scale(0.9);
    }
  `,
  dropdownIn: keyframes`
    0% {
      opacity: 0;
      transform: translateY(-10px);
      transform-origin: top center;
    }
    60% {
      opacity: 0.6;
      transform: translateY(-2px);
      transform-origin: top center;
    }
    100% {
      opacity: 1;
      transform: translateY(0px);
      transform-origin: top center;
    }
  `,
  dropdownOut: keyframes`
    0% {
      opacity: 1;
      transform: translateY(0px);
      transform-origin: top center;
    }
    75% {
      opacity: 0.95;
      transform: translateY(-2px);
      transform-origin: top center;
    }
    100% {
      opacity: 0;
      transform: translateY(-10px);
      transform-origin: top center;
    }
  `,
  growDownIn: keyframes`
    0% {
      transform-origin: top left;
      transform: scale(0.96);
      opacity: 0;
    }
    100% {
      transform-origin: top left;
      transform: scale(1);
      opacity: 1;
    }
  `,
  growDownOut: keyframes`
    0% {
      transform-origin: top left;
      transform: scale(1);
      opacity: 1;
    }
    100% {
      transform-origin: top left;
      transform: scale(0.96);
      opacity: 0;
    }
  `,
};

export const modalStyle = css`
  border: 1px solid ${(props) => props.theme.colors.gray300};
  box-shadow: 0px 2px 16px ${(props) => props.theme.colors.FD08};
  border-radius: ${(props) => props.theme.radius.modal}px;
  background-color: ${(props) => props.theme.colors.gray100};
`;

export const ModalStyle = (props: HTMLAttributes<HTMLDivElement>) => {
  const { className, children, ...rest } = props;
  return (
    <div
      {...rest}
      className={cn(
        'border border-gray-300 shadow-md rounded-md bg-gray-100',
        className,
      )}
    >
      {children}
    </div>
  );
};

export const fullModalStyle = css`
  height: calc(100vh - 48px);
  width: calc(100vw - 48px);
`;

export const detailModalStyle = css`
  transition: right 0.2s;
  background-color: ${(props) => props.theme.colors.gray100};
  box-shadow: 0px -4px 16px ${(props) => props.theme.colors.shadow};
  z-index: ${zIndex.detailModal};
  animation: ${animation.slideIn} 300ms;
`;

type GlobalWrapperProps = {
  width?: string;
  height?: string;
};

export const GlobalModalWrapper = styled.div<GlobalWrapperProps>`
  position: fixed;
  top: 32px;
  right: 32px;
  width: ${({ width }) => width || 'calc(100% - 252px)'};
  height: ${({ height }) => height || 'calc(100% - 64px)'};
  background-color: ${(props) => props.theme.colors.gray100};
  border-radius: 4px;
  z-index: ${zIndex.modal};
`;

export const FadeGradient = (color?: string, degree?: number) => css`
  background: linear-gradient(
    ${degree || 180}deg,
    ${(props) => getAlphaColor(color || props.theme.colors.gray100, 0)} 9.33%,
    ${(props) => color || props.theme.colors.gray100} 82.17%
  );
`;

export const bgHover = css`
  transition: all 0.2s;
  &:hover {
    background-color: ${(props) => props.theme.colors.hover};
  }
`;

export const boxStyle = css`
  border: 1px solid ${({ theme }) => theme.colors.gray400};
  border-radius: 6px;
`;

export const cardStyle = css`
  flex: 1;
  flex-shrink: 0;
  border: 1px solid ${(props) => props.theme.colors.gray400};
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.2s;
  &:hover {
    border-color: ${(props) => props.theme.colors.primary100} !important;
  }
`;
